import { Link } from "react-router-dom";
import {
  ArrowRight, Users, Rocket, Layers, CheckCircle2, Sparkles,
  Cpu, HeartPulse, Landmark, Briefcase, Building2, Server,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import FAQ from "@/components/FAQ";

const HERO_IMG = "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1400&q=80";

const models = [
  {
    icon: Users, color: "var(--accent-teal)",
    title: "Enterprise RPO",
    tag: "Full-scope",
    body: "We own your entire recruitment function. From job briefs to offers, our team operates as your internal talent acquisition department — fully embedded, branded as you, and accountable to your hiring metrics.",
    best: "Companies of 50–500 employees scaling aggressively",
  },
  {
    icon: Rocket, color: "var(--accent-purple)",
    title: "Project RPO",
    tag: "Fixed scope",
    body: "Need to hire 10, 20, or 50 people for a specific initiative or expansion? We build a dedicated team for the project duration — then stand it down when you're done. Fixed scope, fixed timeline, fixed cost.",
    best: "Product launches, market entries, office expansions",
  },
  {
    icon: Layers, color: "var(--accent-mint)",
    title: "Hybrid RPO",
    tag: "Flex capacity",
    body: "We augment your existing TA team — handling overflow volume, specialist roles, or geographic markets your internal team can't reach. You keep control of your core hiring; we handle the rest.",
    best: "Companies with an internal TA team that needs flex capacity",
  },
];

const included = [
  "Dedicated Ashren recruiter(s) embedded in your team",
  "Branded candidate experience — your employer brand, our process",
  "Job description creation and optimisation",
  "Multi-channel sourcing (LinkedIn, job boards, referrals, passive)",
  "Screening, shortlisting, and assessment coordination",
  "Interview scheduling and candidate management",
  "Offer management and pre-employment checks",
  "Onboarding coordination and Day 1 readiness",
  "Weekly reporting: time-to-fill, pipeline health, offer acceptance",
  "Bilingual EN/FR capability for Quebec and federal hiring",
  "PIPEDA-compliant candidate data handling throughout",
  "ATS integration — we work with your existing systems",
];

const differentiators = [
  { title: "Canada-First", body: "We are a Canadian company built for the Canadian talent market. We understand provincial employment law differences, bilingual requirements, and the competitive technology and healthcare talent landscape coast to coast." },
  { title: "Specialist Expertise", body: "Our RPO practice is deepest in Technology, AI & Data, Salesforce, and Healthcare — the roles that take the longest to fill and cost the most when left vacant." },
  { title: "Speed Without Compromise", body: "We don't sacrifice quality for speed. Our pre-vetted networks and structured screening processes mean you see better candidates, faster — typically first submissions within 48–72 hours of job brief." },
  { title: "Fully Compliant", body: "BC Licensed Agency · Ontario THA Licensed · PIPEDA Compliant. Your RPO function is built on a compliant, auditable foundation from day one." },
  { title: "Transparent Pricing", body: "No hidden fees. No per-head charges buried in the contract. Ashren RPO is priced as a monthly managed service with a clear scope — making budget planning simple." },
];

const industries = [
  { icon: Cpu, label: "Technology & AI" },
  { icon: Landmark, label: "Financial Services" },
  { icon: HeartPulse, label: "Healthcare & Life Sciences" },
  { icon: Briefcase, label: "Professional Services" },
  { icon: Building2, label: "Government & Public Sector" },
  { icon: Server, label: "SaaS & Product Companies" },
];

const tiers = [
  {
    name: "Starter RPO", price: "From $4,500", per: "/ month", accent: "var(--accent-teal)",
    features: ["1 dedicated recruiter", "Up to 5 active roles", "Monthly reporting"],
  },
  {
    name: "Growth RPO", price: "From $8,000", per: "/ month", accent: "var(--accent-purple)", featured: true,
    features: ["2 dedicated recruiters", "Up to 15 active roles", "Weekly reporting + analytics dashboard"],
  },
  {
    name: "Enterprise RPO", price: "Custom SOW", per: "", accent: "var(--accent-mint)",
    features: ["Dedicated recruitment pod", "Unlimited active roles", "Full ATS integration + executive reporting"],
  },
];

const faqs = [
  { q: "How is RPO different from using a staffing agency?", a: "A staffing agency fills individual roles on a transactional basis. An RPO partner owns your hiring process end-to-end — operating as part of your team, working to your employer brand, and accountable to your talent-acquisition KPIs. You get a hiring function, not just a resume." },
  { q: "How quickly can an Ashren RPO team be deployed?", a: "We can have a dedicated recruiter embedded and operational within 5–7 business days of contract signing. Larger deployments (2+ recruiters) typically take 10–14 days." },
  { q: "Do you work with our existing ATS?", a: "Yes. We integrate with all major ATS platforms including Workday, Greenhouse, Lever, BambooHR, and iCIMS. If you don't have an ATS, we can recommend and implement one as part of the engagement." },
  { q: "Is RPO right for a company our size?", a: "RPO works for organisations from 50 employees upward. Project RPO works even for smaller companies going through a rapid growth phase. Book a consultation and we'll tell you honestly whether RPO is the right solution — or whether a traditional staffing model would serve you better." },
  { q: "What roles do you specialise in for RPO?", a: "Technology (AI/ML, Data, Cloud, Software, Salesforce), Healthcare (RN, LPN, HCA), and Professional Services. We don't operate generalist RPO across all roles — we focus where our specialist expertise adds the most value." },
];

export default function RPO() {
  return (
    <div data-testid="rpo-page">
      <PageHero
        eyebrow="Recruitment Process Outsourcing · Canada"
        title={<>Your hiring. Our process. <span className="italic" style={{ color: "var(--accent-teal)" }}>Better results.</span></>}
        subtitle="Ashren's RPO solution embeds our recruitment expertise directly into your organisation — giving you a dedicated, scalable hiring function without the overhead of building one internally. From a single division to your entire Canadian workforce, we become your talent-acquisition team."
        image={HERO_IMG}
        imageAlt="RPO team collaboration"
        testId="rpo-hero"
      />

      {/* Trust bar */}
      <section className="bg-white border-b border-[color:var(--border)] py-4" data-testid="rpo-trust-bar">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs md:text-sm text-[color:var(--muted)]">
          {["BC Licensed Agency", "Ontario THA Licensed", "PIPEDA Compliant", "Bilingual EN/FR", "Pan-Canadian Coverage"].map((t) => (
            <div key={t} className="flex items-center gap-2">
              <CheckCircle2 size={14} style={{ color: "var(--accent-mint)" }} />
              {t}
            </div>
          ))}
        </div>
      </section>

      {/* What is RPO */}
      <section className="py-20 md:py-24" data-testid="rpo-what">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="eyebrow mb-3">Definition</div>
            <h2 className="display-lg">What is Recruitment Process Outsourcing?</h2>
          </div>
          <div className="md:col-span-7 text-[color:var(--muted)] leading-relaxed text-lg space-y-4">
            <p>
              Recruitment Process Outsourcing (RPO) is when a company transfers all or part of
              its recruitment function to an external provider. Unlike a traditional staffing
              agency that fills individual roles, an RPO partner owns the entire process — from
              workforce planning and job-brief intake through to candidate screening, interview
              coordination, offer management, and onboarding.
            </p>
            <p>
              Ashren&apos;s RPO model is built for Canadian organisations that want to hire better,
              hire faster, and hire at scale — without the cost and complexity of maintaining a
              large internal TA team.
            </p>
          </div>
        </div>
      </section>

      {/* Three models */}
      <section className="py-20 md:py-24 bg-white border-y border-[color:var(--border)]" data-testid="rpo-models">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow mb-3">Three engagement models</div>
            <h2 className="display-lg">One partner. Every hiring scenario.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {models.map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={m.title} className="practice-card h-full flex flex-col" style={{ "--accent": m.color }} data-testid={`rpo-model-${i}`}>
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
                       style={{ background: `${m.color}22`, color: m.color }}>
                    <Icon size={22} />
                  </div>
                  <div className="eyebrow mb-2" style={{ color: m.color }}>{m.tag}</div>
                  <h3 className="font-display text-xl mb-3">{m.title}</h3>
                  <p className="text-sm text-[color:var(--muted)] leading-relaxed flex-1">{m.body}</p>
                  <div className="mt-5 pt-4 border-t border-[color:var(--border)]">
                    <div className="text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] mb-1">Best for</div>
                    <div className="text-sm text-[color:var(--primary-dark)]">{m.best}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-20 md:py-24" data-testid="rpo-included">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="eyebrow mb-3" style={{ color: "var(--accent-teal)" }}>What&apos;s included</div>
            <h2 className="display-lg">Everything your TA function needs — on day one.</h2>
          </div>
          <div className="md:col-span-8">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {included.map((f) => (
                <li key={f} className="flex items-start gap-3 text-[color:var(--primary-dark)]">
                  <CheckCircle2 size={20} className="shrink-0 mt-0.5" style={{ color: "var(--accent-mint)" }} />
                  <span className="text-sm leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Why Ashren */}
      <section className="py-20 md:py-24 bg-white border-y border-[color:var(--border)]" data-testid="rpo-why">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="max-w-2xl mb-14">
            <div className="eyebrow mb-3">Why Ashren</div>
            <h2 className="display-lg">Five reasons Canadian scaleups pick us for RPO.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {differentiators.map((d, i) => (
              <div key={d.title} className="card h-full" data-testid={`rpo-diff-${i}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold" style={{ background: "rgba(29,180,222,0.14)", color: "var(--accent-teal)" }}>
                    {i + 1}
                  </div>
                  <h3 className="font-display text-lg">{d.title}</h3>
                </div>
                <p className="text-sm text-[color:var(--muted)] leading-relaxed">{d.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 md:py-24" data-testid="rpo-industries">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="max-w-2xl mb-10">
            <div className="eyebrow mb-3">Industries we support</div>
            <h2 className="display-lg">Deep bench across Canada&apos;s highest-demand sectors.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {industries.map((ind) => {
              const Icon = ind.icon;
              return (
                <div key={ind.label} className="flex items-center gap-3 p-4 rounded-xl border border-[color:var(--border)] bg-white" data-testid={`rpo-industry-${ind.label}`}>
                  <Icon size={20} style={{ color: "var(--accent-purple)" }} />
                  <span className="text-sm font-medium">{ind.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 md:py-24 bg-white border-y border-[color:var(--border)]" data-testid="rpo-pricing">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="max-w-2xl mb-4">
            <div className="eyebrow mb-3" style={{ color: "var(--accent-mint)" }}>Simple, predictable pricing</div>
            <h2 className="display-lg">Monthly managed service — not per-hire fees.</h2>
            <p className="mt-4 text-[color:var(--muted)] leading-relaxed">
              Unlike traditional agency fees of 15–25% per hire, Ashren RPO is structured as a
              monthly managed service — giving you unlimited hiring capacity within the agreed scope.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {tiers.map((t) => (
              <div key={t.name}
                   className={`card h-full flex flex-col relative ${t.featured ? "ring-2" : ""}`}
                   style={t.featured ? { "--tw-ring-color": t.accent, boxShadow: `0 20px 40px -20px ${t.accent}55` } : {}}
                   data-testid={`rpo-tier-${t.name}`}>
                {t.featured && (
                  <div className="absolute -top-3 left-6 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ background: t.accent }}>
                    <Sparkles size={12} className="inline mr-1" /> Most popular
                  </div>
                )}
                <div className="eyebrow mb-3" style={{ color: t.accent }}>{t.name}</div>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="font-display text-3xl md:text-4xl text-[color:var(--primary-dark)]">{t.price}</span>
                  {t.per && <span className="text-[color:var(--muted)] text-sm">{t.per}</span>}
                </div>
                <ul className="space-y-3 mb-6 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex gap-2 text-sm">
                      <CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: t.accent }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/for-employers" className="btn-primary w-full justify-center" data-testid={`rpo-tier-cta-${t.name}`}>
                  Book a free consultation
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-8 text-xs text-[color:var(--muted)] text-center max-w-3xl mx-auto">
            All plans include PIPEDA-compliant data handling, bilingual EN/FR capability, and
            compliance with BC and Ontario employment standards.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24" data-testid="rpo-faq-section">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="eyebrow mb-3">Common questions</div>
            <h2 className="display-lg">RPO, answered.</h2>
          </div>
          <div className="md:col-span-8">
            <FAQ items={faqs} testId="rpo-faq" />
          </div>
        </div>
      </section>

      {/* Cross-sell to EOR */}
      <section className="py-14 bg-white border-y border-[color:var(--border)]" data-testid="rpo-cross-sell">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
          <div className="max-w-2xl">
            <div className="eyebrow mb-2" style={{ color: "var(--accent-purple)" }}>Also from Ashren</div>
            <h3 className="font-display text-2xl md:text-3xl mb-2">Need an Employer of Record too?</h3>
            <p className="text-[color:var(--muted)] leading-relaxed">
              If you&apos;re hiring workers who need to be legally employed in Canada — in provinces
              where you don&apos;t have an entity — Ashren EOR handles the compliance while RPO
              handles the sourcing.
            </p>
          </div>
          <Link to="/eor" className="btn-secondary shrink-0" data-testid="rpo-cross-sell-eor">
            Learn about Ashren EOR <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <CTABand
        title="Ready to hand off your hiring?"
        subtitle="Book a free 30-minute RPO consultation. We'll assess your current hiring process, identify the gaps, and recommend the right RPO model for your organisation — no obligation."
        primaryLabel="Book free consultation"
        primaryTo="/for-employers"
        secondaryLabel="Contact us"
        secondaryTo="/contact"
      />
    </div>
  );
}
